import { useQuery } from '@apollo/client/react';
import { TestDataService } from '@teamexos/test-data-service';
import { useEffect, useState } from 'react';
import { clientOptions } from '../../../apollo/client';
import { AdminGetCoachUsersByClientDocument } from '../../../graphql/types';

export const initializeTestDataService = async () => {
  const { clientUrl } = clientOptions;
  const selfToken = await clientOptions.getToken();
  if (!clientUrl || !selfToken) {
    throw new Error('clientUrl or token is not set');
  }
  return TestDataService.initTestDataGraphqlClient({
    clientUrl,
    authToken: selfToken,
  });
};

export const useTestDataService = () => {
  useEffect(() => {
    (async () => {
      await initializeTestDataService();
    })();
  }, []);
};

export interface MemberCreationStatusItem {
  id: string;
  label: string;
  status: 'loading' | 'done' | 'error';
  message?: string;
}

export const useActionLog = () => {
  const [memberCreationStatus, setMemberCreationStatus] = useState<
    MemberCreationStatusItem[]
  >([]);
  const [memberCreationProgress, setMemberCreationProgress] = useState<
    number[]
  >([]);
  const [errors, setErrors] = useState<string[]>([]);

  const logAction = async <T>(
    status: string,
    action: () => Promise<T>,
    retrying = false,
  ): Promise<T | null> => {
    const id = crypto.randomUUID();
    setMemberCreationStatus((p) => [
      { id, label: status, status: 'loading' },
      ...p,
    ]);
    try {
      const result = await action();
      setMemberCreationStatus((p) => {
        const newStatus = [...p];
        const index = newStatus.findIndex((s) => s.id === id);
        if (index !== -1) {
          newStatus[index] = { id, label: status, status: 'done' };
        }
        return newStatus;
      });
      return result;
    } catch (e) {
      setMemberCreationStatus((p) => {
        const newStatus = [...p];
        const index = newStatus.findIndex((s) => s.id === id);
        const errorItem: MemberCreationStatusItem = {
          id,
          label: status,
          status: 'error',
          message: e.message,
        };
        if (index !== -1) {
          newStatus[index] = errorItem;
        } else {
          newStatus.unshift(errorItem);
        }
        return newStatus;
      });

      if (e.message.includes('Unauthorized')) {
        if (!retrying) {
          await logAction('Token expired, reinitializing client', () =>
            initializeTestDataService(),
          );
          return logAction(`Retrying: ${status}`, action, true);
        }
        setErrors((p) => [...p, 'Could not reinitialize client']);
      }

      return null;
    }
  };

  return {
    logAction,
    memberCreationStatus,
    setMemberCreationStatus,
    memberCreationProgress,
    setMemberCreationProgress,
    errors,
    setErrors,
  };
};

export const useRequiredClientMembers = (
  requiredClientIds: string[],
  coachId?: string | null,
) => {
  const { data: client1CoachUsers, refetch: refetch1 } = useQuery(
    AdminGetCoachUsersByClientDocument,
    {
      variables: {
        clientId: requiredClientIds[0],
        id: coachId || '',
      },
      skip: !coachId,
    },
  );
  const { data: client2CoachUsers, refetch: refetch2 } = useQuery(
    AdminGetCoachUsersByClientDocument,
    {
      variables: {
        clientId: requiredClientIds[1],
        id: coachId || '',
      },
      skip: !coachId,
    },
  );
  const { data: client3CoachUsers, refetch: refetch3 } = useQuery(
    AdminGetCoachUsersByClientDocument,
    {
      variables: {
        clientId: requiredClientIds[2],
        id: coachId || '',
      },
      skip: !coachId,
    },
  );
  return {
    data: [
      {
        clientId: requiredClientIds[0],
        stubUsers: client1CoachUsers?.stubUsers?.coachData?.coachUsers,
        predefinedUsers:
          client1CoachUsers?.predefinedUsers?.coachData?.coachUsers,
      },
      {
        clientId: requiredClientIds[1],
        stubUsers: client2CoachUsers?.stubUsers?.coachData?.coachUsers,
        predefinedUsers:
          client2CoachUsers?.predefinedUsers?.coachData?.coachUsers,
      },
      {
        clientId: requiredClientIds[2],
        stubUsers: client3CoachUsers?.stubUsers?.coachData?.coachUsers,
        predefinedUsers:
          client3CoachUsers?.predefinedUsers?.coachData?.coachUsers,
      },
    ],
    refetch: async () =>
      await Promise.all([refetch1(), refetch2(), refetch3()]),
  };
};
